// NesjsCoreBase — everything the browser (./web.ts) and headless
// (./headless.ts) adapters share. Both wrap the same vendored
// angelo-wf/NesJs core; the tier adapters add only their genuinely
// tier-specific surface (background/CHR introspection for the
// devtools panel, RAM snapshots for the test harness).
//
// Notable bits of the adapter:
//
//   - Audio is pull-based (`getSamples(buf, count)`). We pull once per
//     frame and accumulate mono → interleaved stereo.
//   - The framebuffer is written as RGBA bytes via `getPixels(buf)`.
//     We share the u32 framebuffer's ArrayBuffer with a Uint8 view so
//     getPixels fills our buffer directly — no per-frame copy.
//   - There's no flat 64K CPU memory. Reads use `nes.peak`
//     (side-effect-free); writes to the $0000–$1FFF RAM mirror hit
//     `nes.ram` directly. Writes outside RAM are dropped (Munos
//     scripts shouldn't poke PPU/APU registers).
//   - OAM-DMA detection uses the first-class `nes.onwrite` hook,
//     filtered to $4014.
import { Nes } from "./vendor/index.js";
import { assertSaveStateCore, CpuReg, } from "@multinostalgia/core-contract";
// Registry id this core's savestates are tagged with — matches the
// `id` exported by both tier adapters (./web.ts, ./headless.ts).
const CORE_ID = "nesjs";
const SCREEN_W = 256;
const SCREEN_H = 240;
// NTSC NES runs at ~60.0988 fps; for a target sample rate R, one
// frame's worth of samples is ~R/60. NesJs resamples internally to
// whatever `count` we ask for, so we request the exact per-frame
// budget.
function samplesPerFrame(sampleRate) {
    return Math.round(sampleRate / 60);
}
// One NTSC frame is ~29780.5 CPU cycles; the APU buffers exactly one
// frame of per-cycle output (29781 samples), so a synth step clocks at
// most this many cycles before draining, to never overrun that buffer.
const NES_CYCLES_PER_FRAME = 29780;
// Ceiling on the undrained audio buffer. The host drains every frame in normal
// use, but a consumer that enables sound and never drains would otherwise grow
// audioAccum without bound. ~5s of interleaved stereo @48kHz — past it we skip
// producing a frame's audio rather than accumulate forever.
const MAX_AUDIO_ACCUM_SAMPLES = 480_000;
// The chip this core synthesizes (AudioSynthCapable). Frozen singleton.
const NES_CHIPS = Object.freeze(["nes-2a03"]);
// Length of the per-scanline CHR snapshot timeline. Only visible
// scanlines (0..239) are tracked — that's the range sprite-matching
// callers can meaningfully reference.
export const VISIBLE_SCANLINES = 240;
export class NesjsCoreBase {
    system = "nes";
    screenWidth = SCREEN_W;
    screenHeight = SCREEN_H;
    // 60 exactly — NOT NTSC's true 60.0988 fps. This core's per-frame audio
    // budget is round(sampleRate/60) (see samplesPerFrame), so 60 is the pacing
    // rate that keeps `frameRateHz * samplesPerFrame == sampleRate` exact and
    // drift-free. See BaseCore.frameRateHz.
    frameRateHz = 60;
    framebuffer = new Uint32Array(SCREEN_W * SCREEN_H);
    // Byte view over framebuffer.buffer so getPixels() fills our buffer
    // directly. NesJs writes RGBA bytes per pixel; the u32 readback is
    // 0xFFBBGGRR in little-endian — the canonical core layout
    // (canvas-display only cares about the low 24 bits and OR's alpha
    // to 0xff).
    framebufferBytes;
    nes;
    audioEnabled;
    renderPixels;
    sampleRate;
    samplesPerFrameCount;
    monoBuf;
    audioAccum = [];
    oamDmaHandler = null;
    // Observer for the game's APU-register writes (AudioCaptureCapable) —
    // taps the same nes.onwrite hook the OAM-DMA detector uses.
    audioCaptureHandler = null;
    // Set once the APU has been initialized for standalone synth use (no
    // ROM / CPU) — see synthSamples / AudioSynthCapable.
    synthReady = false;
    // ----- execution-hook state (ExecHookCapable) -----
    //
    // The per-instruction trap (`nes.onexecute`) is installed only while
    // ≥1 hook is armed; the armed-offset sets give an O(1) "does this PC
    // match anything?" check so the common (no-hit) path is a Set.has, not
    // a map walk. See docs/execution-hooks-and-bank-switching.md §8.1.
    execHooks = new Map();
    execHookNextId = 1;
    armedPrgOffsets = new Set();
    armedCpuAddrs = new Set();
    execTrapInstalled = false;
    // chrTimeline[N] = the mapper's CHR bank state at the start of
    // scanline N during the most recently completed frame. Fed by the
    // nes.onScanlineStart hook; consumed by readTilePixelsAtScanline.
    // Always-on — the overhead is one int compare per ppu cycle plus a
    // ~7-byte snapshot per scanline, and "always available" means
    // consumers never race against an enable flag. For non-CHR-banking
    // mappers (NROM, UxROM, AxROM) every entry is null and the read
    // path silently degrades to current-state reads — correct because
    // those mappers have nothing to swap.
    chrTimeline = new Array(VISIBLE_SCANLINES).fill(null);
    constructor(opts) {
        this.framebufferBytes = new Uint8Array(this.framebuffer.buffer);
        this.audioEnabled = opts.emulateSound;
        this.renderPixels = opts.renderPixels;
        this.sampleRate = opts.sampleRate;
        this.samplesPerFrameCount = samplesPerFrame(opts.sampleRate);
        this.monoBuf = new Float32Array(this.samplesPerFrameCount);
        this.nes = new Nes();
        // Wire the OAM-DMA detector unconditionally; the handler reads
        // `this.oamDmaHandler` lazily so registering later still works.
        this.nes.onwrite = (adr, val) => {
            if (adr === 0x4014) {
                const h = this.oamDmaHandler;
                if (h)
                    h();
            }
            // APU register writes ($4000-$4017, excluding OAM-DMA $4014 and
            // controller $4016) feed the capture observer when one is armed.
            const cap = this.audioCaptureHandler;
            if (cap && adr >= 0x4000 && adr <= 0x4017 && adr !== 0x4014 && adr !== 0x4016) {
                cap(adr - 0x4000, val & 0xff);
            }
        };
        // Capture CHR bank state at every visible scanline transition —
        // needed for SMB3-class mid-frame CHR banking, where a sprite
        // drawn before the status-bar IRQ must resolve against the
        // gameplay bank rather than the post-IRQ status-bar bank.
        this.nes.onScanlineStart = (line) => {
            if (line >= VISIBLE_SCANLINES)
                return;
            const mapper = this.nes.mapper;
            this.chrTimeline[line] = mapper?.snapshotChrState?.() ?? null;
        };
    }
    // ----- lifecycle -----
    loadROM(bytes) {
        const ok = this.nes.loadRom(bytes);
        if (!ok) {
            // NesJs logs to console internally; surface a structured error
            // here so the host's catch path has something to report.
            throw new Error(`${this.constructor.name}: loadRom failed (invalid or unsupported ROM)`);
        }
        // Upstream NesJs's Cpu constructor calls cpu.reset() before any
        // mapper exists, so PC defaults to 0 (see cpu.js:45). loadRom
        // itself doesn't re-reset — upstream's frontend does it explicitly
        // after loadRom (js/main.js:149: `nes.reset(true)`). Without this
        // the CPU runs BRK from RAM forever and the game never boots.
        // Hard reset zeros RAM; soft would leave whatever happened to be
        // there from a prior load.
        this.nes.reset(true);
    }
    frame() {
        this.nes.runFrame();
        // getPixels is a pure palette/emphasis readback into framebufferBytes
        // — it mutates no emulation state, so skipping it when no consumer
        // reads the framebuffer is safe and removes a large per-frame cost.
        if (this.renderPixels)
            this.nes.getPixels(this.framebufferBytes);
        if (this.audioEnabled && this.audioAccum.length < MAX_AUDIO_ACCUM_SAMPLES) {
            this.nes.getSamples(this.monoBuf, this.samplesPerFrameCount);
            // Expand mono → interleaved stereo. NesJs's mixer emits samples
            // roughly in [0, 0.85] (sum of small positive APU contributions
            // — see nes/apu.js:mix()). Centering on 0 would need a running
            // DC blocker; we let the audio sink handle the DC settle on its
            // own. Push as-is, L=R.
            for (let i = 0; i < this.samplesPerFrameCount; i++) {
                const s = this.monoBuf[i];
                this.audioAccum.push(s, s);
            }
        }
    }
    // ----- input -----
    //
    // NesJs's button numbering is A=0, B=1, SELECT=2, START=3, UP=4,
    // DOWN=5, LEFT=6, RIGHT=7 — matches the contract.
    buttonDown(controller, button) {
        this.nes.setButtonPressed(controller, button);
    }
    buttonUp(controller, button) {
        this.nes.setButtonReleased(controller, button);
    }
    // ----- audio -----
    drainAudio() {
        if (this.audioAccum.length === 0)
            return null;
        const out = new Float32Array(this.audioAccum);
        this.audioAccum.length = 0;
        return out;
    }
    // ----- audio synthesis (AudioSynthCapable) -----
    //
    // Drive the 2A03 APU as a standalone synthesizer with no ROM and no CPU
    // running: a VGM player writes registers via writeAudioRegister and asks
    // for samples via synthSamples. The same per-cycle apu.cycle() path the
    // CPU uses in runFrame produces the audio; getSamples resamples it. DMC
    // sample playback is the one gap — it would fetch bytes from a ROM that
    // a synth has none of — so v1 synth covers pulse/triangle/noise.
    audioChips() {
        return NES_CHIPS;
    }
    writeAudioRegister(reg, value) {
        this.ensureSynth();
        this.nes.apu.write((0x4000 + (reg & 0xff)) & 0xffff, value & 0xff);
    }
    synthSamples(samples) {
        this.ensureSynth();
        let remaining = samples | 0;
        const per = this.samplesPerFrameCount;
        while (remaining > 0) {
            const n = Math.min(remaining, per);
            // Clock the APU by the cycle-fraction of a frame these samples
            // cover — capped at one frame so the APU's per-frame output buffer
            // never overruns — then resample those raw cycles down to n.
            const cycles = Math.round((n / per) * NES_CYCLES_PER_FRAME);
            for (let i = 0; i < cycles; i++)
                this.nes.apu.cycle();
            this.nes.getSamples(this.monoBuf, n);
            for (let i = 0; i < n; i++) {
                const s = this.monoBuf[i];
                this.audioAccum.push(s, s);
            }
            remaining -= n;
        }
    }
    // Initialize the APU's register/timer state the first time the synth
    // path is used. The normal reset() needs a mapper (none exists in a
    // synth instance), so reset just the APU.
    ensureSynth() {
        if (this.synthReady)
            return;
        this.nes.apu.reset();
        this.synthReady = true;
    }
    // ----- audio capture (AudioCaptureCapable) -----
    onAudioRegisterWrite(handler) {
        this.audioCaptureHandler = handler;
    }
    // Reconstruct the 2A03's current channel setup as frame-0 prime writes
    // for a VGM capture (see AudioCaptureCapable.audioPrimeState). $4015
    // (channel enable) goes first — it un-gates the length counters, so a
    // channel enabled before the capture started is otherwise silent on a
    // freshly-reset synth APU. Then the continuous voicing registers (duty/
    // volume/sweep, timer low, noise period). Trigger / length-load
    // registers ($4003/$4007/$400b/$400f) and the frame-counter ($4017) are
    // omitted: replaying a trigger would resurrect an already-ended note,
    // and the game re-writes $4017 in-window anyway. DMC ($4010-$4013) is
    // skipped because the synth doesn't play it.
    audioPrimeState() {
        const regs = this.nes.apu.snapshotRegisters();
        const out = [];
        const enable = regs[0x15] ?? 0;
        if (enable !== 0)
            out.push({ reg: 0x15, value: enable });
        // pulse1: $4000-$4002 · pulse2: $4004-$4006 · triangle: $4008,$400a ·
        // noise: $400c,$400e
        const VOICING = [0x00, 0x01, 0x02, 0x04, 0x05, 0x06, 0x08, 0x0a, 0x0c, 0x0e];
        for (const reg of VOICING) {
            const value = regs[reg] ?? 0;
            if (value !== 0)
                out.push({ reg, value });
        }
        return out;
    }
    // ----- hooks -----
    onOamDmaWrite(handler) {
        this.oamDmaHandler = handler;
    }
    // ----- execution hooks (ExecHookCapable) -----
    onExecHook(target, handler, priority = 0) {
        const id = this.execHookNextId++;
        this.execHooks.set(id, { region: target.region, offset: target.offset, handler, priority });
        if (target.region === "PRG_ROM")
            this.armedPrgOffsets.add(target.offset);
        else if (target.region === "CPU")
            this.armedCpuAddrs.add(target.offset);
        // PRG_RAM hooks are accepted but never fire in v1 (matching deferred,
        // per the spec); they sit inert in the map until cleared.
        this.installExecTrap();
        return id;
    }
    clearExecHook(handle) {
        const hook = this.execHooks.get(handle);
        if (!hook)
            return;
        this.execHooks.delete(handle);
        // Drop the offset from its armed set only if no surviving hook shares
        // the same region+offset (two hooks may target one location).
        const stillArmed = (region, offset) => {
            for (const h of this.execHooks.values()) {
                if (h.region === region && h.offset === offset)
                    return true;
            }
            return false;
        };
        if (hook.region === "PRG_ROM" && !stillArmed("PRG_ROM", hook.offset)) {
            this.armedPrgOffsets.delete(hook.offset);
        }
        else if (hook.region === "CPU" && !stillArmed("CPU", hook.offset)) {
            this.armedCpuAddrs.delete(hook.offset);
        }
        if (this.execHooks.size === 0)
            this.uninstallExecTrap();
    }
    translatePC(cpuAddr) {
        const a = cpuAddr & 0xffff;
        const mapper = this.nes.mapper;
        if (a >= 0x8000 && mapper)
            return { region: "PRG_ROM", offset: mapper.getRomAdr(a) };
        if (a >= 0x6000 && a < 0x8000)
            return { region: "PRG_RAM", offset: a & 0x1fff };
        return { region: "CPU", offset: a };
    }
    readCpuReg(reg) {
        const cpu = this.nes.cpu;
        switch (reg) {
            case CpuReg.A: return cpu.r[0];
            case CpuReg.X: return cpu.r[1];
            case CpuReg.Y: return cpu.r[2];
            case CpuReg.SP: return cpu.r[3];
            case CpuReg.P: return cpu.getP(false);
            case CpuReg.PC: return cpu.br[0];
            default: return 0;
        }
    }
    writeCpuReg(reg, val) {
        const cpu = this.nes.cpu;
        // Uint8Array / Uint16Array assignments mask to width; P goes through
        // setP so the flag booleans stay consistent.
        switch (reg) {
            case CpuReg.A:
                cpu.r[0] = val;
                break;
            case CpuReg.X:
                cpu.r[1] = val;
                break;
            case CpuReg.Y:
                cpu.r[2] = val;
                break;
            case CpuReg.SP:
                cpu.r[3] = val;
                break;
            case CpuReg.P:
                cpu.setP(val & 0xff);
                break;
            case CpuReg.PC:
                cpu.br[0] = val;
                break;
            default: break;
        }
    }
    writeRomByte(coord, val) {
        const mapper = this.nes.mapper;
        if (!mapper)
            return;
        const v = val & 0xff;
        if (coord.region === "PRG_ROM") {
            mapper.rom[mapper.h.base + (coord.offset & mapper.h.prgAnd)] = v;
        }
        else if (coord.region === "CPU" && coord.offset >= 0x8000) {
            mapper.rom[mapper.h.base + (mapper.getRomAdr(coord.offset) & mapper.h.prgAnd)] = v;
        }
        // PRG_RAM / sub-$8000 CPU coords are not part of the ROM image; ignored.
    }
    installExecTrap() {
        if (this.execTrapInstalled)
            return;
        this.nes.onexecute = (pc, _opcode) => this.fireExecHooks(pc);
        this.execTrapInstalled = true;
    }
    uninstallExecTrap() {
        if (!this.execTrapInstalled)
            return;
        this.nes.onexecute = undefined;
        this.execTrapInstalled = false;
    }
    // Fires pre-step on every instruction while armed. Forward-translates
    // the PC and fires any hook whose target matches. Handlers are
    // collected before invocation so a handler may register/clear hooks
    // (mutating the map) without disturbing this dispatch.
    fireExecHooks(pc) {
        const a = pc & 0xffff;
        let prgOff = -1;
        if (this.armedPrgOffsets.size > 0) {
            const mapper = this.nes.mapper;
            if (mapper)
                prgOff = mapper.getRomAdr(a);
        }
        const prgHit = prgOff >= 0 && this.armedPrgOffsets.has(prgOff);
        const cpuHit = this.armedCpuAddrs.size > 0 && this.armedCpuAddrs.has(a);
        if (!prgHit && !cpuHit)
            return;
        let matched = null;
        for (const hook of this.execHooks.values()) {
            const hit = hook.region === "CPU"
                ? (cpuHit && hook.offset === a)
                : (prgHit && hook.region === "PRG_ROM" && hook.offset === prgOff);
            if (hit)
                (matched ??= []).push({ handler: hook.handler, priority: hook.priority });
        }
        if (matched) {
            // Ascending priority => higher fires later (so an "override" hook that
            // shares this location runs after its "base"). Array.sort is stable, and
            // the map yields registration order, so equal priorities keep it. Only
            // pay the sort when >1 hook shares this instruction (the rare case).
            if (matched.length > 1)
                matched.sort((x, y) => x.priority - y.priority);
            for (const m of matched)
                m.handler(pc);
        }
    }
    // ----- CPU memory -----
    //
    // Reads use `nes.peak()` — side-effect-free across all address
    // ranges (RAM, PPU ports, APU ports, mapper). Writes are limited to
    // the RAM mirror at $0000–$1FFF; writes outside are dropped. Munos
    // scripts using read_*/write_* on non-RAM addresses would be a
    // script bug regardless of the core.
    readCpuByte(addr) {
        return this.nes.peak(addr & 0xffff);
    }
    writeCpuByte(addr, val) {
        const a = addr & 0xffff;
        // Full CPU-bus passthrough: write_u8 does exactly what the 6502 does when
        // it stores to this address, with all side effects. Internal work RAM
        // ($0000-$1FFF, written directly to skip the bus mirror math) and cart
        // PRG-RAM ($6000-$7FFF) land as plain bytes; PPU registers ($2000-$2007),
        // APU/IO ($4000-$4017, incl. the $4014 OAM-DMA and the controller strobe
        // the onwrite tap observes), and the $8000-$FFFF mapper window (bank
        // switching) all fire their real effects. A cart with no PRG-RAM no-ops.
        // Editing the cartridge image itself is a different operation — see
        // writeRomByte.
        if (a < 0x2000) {
            this.nes.ram[a & 0x7ff] = val & 0xff;
            return;
        }
        this.nes.write(a, val & 0xff);
    }
    // ----- PPU memory (PpuMemoryCapable) -----
    //
    // Direct poke/peek of the PPU address space ($0000-$3FFF: pattern tables,
    // nametables, palette), bypassing the $2006/$2007 register protocol — no
    // address latch, no vblank window, no VRAM staging buffer. This is the exact
    // routing the real PPUDATA port does (pipu.js case 7): palette above $3F00,
    // everything else through writeInternal (the mapper applies nametable
    // mirroring). Because it writes the emulator's own nametable/palette arrays,
    // the poke shows on the next render. Drives the Munos write_ppu/read_ppu
    // builtins so a mod paints the background without the game's VRAM buffer.
    readPpuByte(ppuAddr) {
        const a = ppuAddr & 0x3fff;
        return a >= 0x3f00 ? this.nes.ppu.readPalette(a) : this.nes.ppu.readInternal(a);
    }
    writePpuByte(ppuAddr, value) {
        const a = ppuAddr & 0x3fff;
        const v = value & 0xff;
        if (a >= 0x3f00) {
            this.nes.ppu.writePalette(a, v);
            return;
        }
        this.nes.ppu.writeInternal(a, v);
    }
    readRamSize() { return 0x8000; }
    // Snapshot of the readable CPU-bus RAM, identity-indexed from $0000
    // (so snapshot[addr] == the byte at CPU $addr — the contract NES
    // analyzers/discriminators rely on). Two real RAM regions are filled:
    //
    //   - $0000-$07FF: the 2KB internal work RAM (nes.ram verbatim).
    //   - $6000-$7FFF: cartridge PRG-RAM / battery SRAM, read through
    //     nes.peak() so it honors the mapper's currently-mapped PRG-RAM
    //     bank and works uniformly across mappers (carts without PRG-RAM
    //     read back as the mapper's open-bus, harmless).
    //
    // The $0800-$5FFF span (internal-RAM mirrors, PPU/APU ports, expansion
    // area) is left zero-filled — it holds no cartridge RAM, and consumers
    // wanting live PPU/APU register or mirror values must use readCpuByte()
    // (the snapshot is a RAM surface, not a full bus dump). A fresh buffer
    // each call so the caller can't mutate emulator state through it.
    readRamSnapshot() {
        const snap = new Uint8Array(0x8000);
        snap.set(this.nes.ram, 0x0000); // internal RAM $0000-$07FF
        for (let a = 0x6000; a < 0x8000; a++) {
            snap[a] = this.nes.peak(a); // cartridge PRG-RAM $6000-$7FFF
        }
        return snap;
    }
    // ----- PPU introspection -----
    readOam() {
        return new Uint8Array(this.nes.ppu.oamRam);
    }
    isSpritesEnabled() {
        return this.nes.ppu.sprRendering;
    }
    isBackgroundEnabled() {
        return this.nes.ppu.bgRendering;
    }
    // Per-pixel background-opacity mask (256x240, row-major): 1 where a
    // non-backdrop background pixel was drawn this frame. Read straight off the
    // PPU's bgOpaque buffer, populated by the MULTINOSTALGIA instrumentation
    // patch (see PATCHES.md / vendor pipu.js) at the same point it writes
    // pixelOutput. Lets a host honor OAM background-priority for overlay
    // sprites. Returns the live buffer (no per-frame copy); callers read it
    // synchronously during compositing and must not retain or mutate it.
    readBackgroundMask() {
        return this.nes.ppu.bgOpaque;
    }
    // Build `sprPalette`: 16 entries, 0x00BBGGRR.
    // Sprite palette is paletteRam[0x10..0x1F], but entries 0x10/0x14/
    // 0x18/0x1C alias back to BG slots 0x00/0x04/0x08/0x0C (per
    // NesJs's readPalette() and the NES hardware spec). We mirror that
    // here so callers see what's actually on-screen.
    readSpritePalette() {
        const ppu = this.nes.ppu;
        const palRam = ppu.paletteRam;
        const nesPal = ppu.nesPal;
        const out = new Uint32Array(16);
        for (let i = 0; i < 16; i++) {
            let palAdr = 0x10 + i;
            if ((palAdr & 0x3) === 0)
                palAdr -= 0x10;
            const colorIdx = (palRam[palAdr] ?? 0) & 0x3f;
            const entry = nesPal[colorIdx] ?? [0, 0, 0];
            const r = entry[0] & 0xff;
            const g = entry[1] & 0xff;
            const b = entry[2] & 0xff;
            out[i] = ((b << 16) | (g << 8) | r) >>> 0;
        }
        return out;
    }
    // Raw NES master-palette indices (0..63) for the 16 sprite-palette
    // slots — same layout and aliasing as readSpritePalette, but the
    // unresolved palette-RAM value rather than its RGB. This is the
    // core-independent color identity the adapter records.
    readSpritePaletteIndices() {
        const palRam = this.nes.ppu.paletteRam;
        const out = new Uint8Array(16);
        for (let i = 0; i < 16; i++) {
            let palAdr = 0x10 + i;
            if ((palAdr & 0x3) === 0)
                palAdr -= 0x10;
            out[i] = (palRam[palAdr] ?? 0) & 0x3f;
        }
        return out;
    }
    // NesJs stores PPUCTRL bit 3 as an absolute base address (0 or
    // 0x1000). Map to the 0/1 flag the contract calls for.
    readSpritePatternTableBase() {
        return this.nes.ppu.spritePatternBase === 0x1000 ? 1 : 0;
    }
    readSpriteHeight() {
        return this.nes.ppu.spriteHeight === 16 ? 16 : 8;
    }
    // Decode an 8x8 tile from CHR. `globalIndex` 0..255 = pattern table
    // 0 ($0000-$0FFF), 256..511 = pattern table 1 ($1000-$1FFF). Each
    // tile is 16 bytes: 8 bytes low bitplane followed by 8 bytes high
    // bitplane. ppuPeak honors the mapper's currently-mapped CHR bank,
    // which matters for MMC3/MMC1 games with bank-switched CHR.
    readTilePixels(globalIndex) {
        const mapper = this.nes.mapper;
        if (!mapper)
            return new Uint8Array(64);
        const base = (globalIndex & 0x1ff) * 16;
        const out = new Uint8Array(64);
        for (let row = 0; row < 8; row++) {
            const lo = mapper.ppuPeak(base + row);
            const hi = mapper.ppuPeak(base + row + 8);
            for (let col = 0; col < 8; col++) {
                const bit = 7 - col;
                const p = ((lo >> bit) & 1) | (((hi >> bit) & 1) << 1);
                out[row * 8 + col] = p;
            }
        }
        return out;
    }
    // Scanline-aware variant. Temporarily restores the mapper's CHR
    // bank state to whatever was active at the start of `scanline`
    // during the most recently completed frame, reads the tile through
    // the mapper, and puts the live state back. Used by the sprite-
    // matching paths so a meta-sprite drawn before SMB3's status-bar
    // IRQ resolves against the gameplay bank rather than the post-IRQ
    // status-bar bank.
    //
    // Degrades to readTilePixels (current state) when:
    //   - scanline is outside [0, 239] (callers may pass e.g. y+9 for
    //     an 8x16 sprite straddling the bottom of the screen);
    //   - the mapper doesn't support snapshot/restore (NROM-class
    //     mappers have nothing to swap, so "current state" is correct);
    //   - the timeline slot is null (no transition captured yet, or
    //     the mapper returned null at snapshot time).
    readTilePixelsAtScanline(globalIndex, scanline) {
        if (scanline < 0 || scanline >= VISIBLE_SCANLINES) {
            return this.readTilePixels(globalIndex);
        }
        const snap = this.chrTimeline[scanline];
        if (snap === null || snap === undefined) {
            return this.readTilePixels(globalIndex);
        }
        const mapper = this.nes.mapper;
        if (!mapper || !mapper.snapshotChrState || !mapper.restoreChrState) {
            return this.readTilePixels(globalIndex);
        }
        const live = mapper.snapshotChrState();
        mapper.restoreChrState(snap);
        try {
            return this.readTilePixels(globalIndex);
        }
        finally {
            mapper.restoreChrState(live);
        }
    }
    // ----- savestate -----
    //
    // The inner blob is NesJs's own getState() object, opaque to
    // callers; the envelope tags it with this core's registry id so
    // loadState can refuse foreign states (SaveStateMismatchError).
    saveState() {
        return { core: CORE_ID, data: this.nes.getState() };
    }
    loadState(state) {
        const data = assertSaveStateCore(state, CORE_ID);
        const ok = this.nes.setState(data);
        if (!ok) {
            throw new Error(`${this.constructor.name}: setState refused the state (version or ROM mismatch)`);
        }
    }
    // ----- battery-backed save RAM (BatteryBackedCapable) -----
    //
    // The cartridge's save file: PRG-RAM $6000-$7FFF on the battery mappers
    // (Zelda's MMC1, etc.). Separate from saveState — this is the small,
    // portable region a real cartridge's battery keeps, not the whole machine.
    // getBattery returns null for a non-battery cart (SMB and friends), which is
    // the host's signal to skip persistence entirely.
    getBattery() {
        const b = this.nes.getBattery();
        if (!b)
            return null;
        return Uint8Array.from(b.data);
    }
    setBattery(data) {
        // nes.setBattery no-ops when the cart has no battery, and the mapper
        // rejects a blob that isn't exactly 0x2000 bytes — a mismatched save is
        // ignored rather than allowed to corrupt PRG-RAM. new Uint8Array(data)
        // inside the mapper copies, so passing our buffer is safe.
        this.nes.setBattery({ data });
    }
}
//# sourceMappingURL=core-base.js.map